import React, { useState } from 'react';
import { Alert, Card, Empty, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useHrmsPolicy } from '@/hooks/useHrmsPolicy';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const { Text } = Typography;

const DocumentExpiryPage: React.FC = () => {
  const [expiringSoon, setExpiringSoon] = useState(true);
  const { indiaPack } = useHrmsPolicy();
  const { empLabel } = useEmployeeOptions({ employment_status: null });

  const params = expiringSoon ? { expiring_soon: true, days: 30 } : undefined;

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.documentExpiry(params),
    queryFn: () => hrmsService.listDocumentExpiry(params),
  });

  const columns: ColumnsType<any> = [
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    { title: 'Type', dataIndex: 'document_type', width: 120 },
    { title: 'Label', dataIndex: 'document_label' },
    { title: 'Number', dataIndex: 'document_number', width: 140 },
    {
      title: 'Expiry',
      dataIndex: 'expiry_date',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
      sorter: (a, b) => dayjs(a.expiry_date).unix() - dayjs(b.expiry_date).unix(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => {
        const color = s === 'Expired' ? 'red' : s === 'Expiring' ? 'orange' : 'green';
        return <Tag color={color}>{s}</Tag>;
      },
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Document expiry"
          subtitle={
            indiaPack
              ? 'Passport, insurance, and other employee document renewals (India KYC attachments)'
              : 'Passport, visa, labour card, and other employee document renewals'
          }
          primaryActions={
            <span>
              <Text type="secondary" style={{ marginRight: 8 }}>
                Expiring within 30 days
              </Text>
              <Switch checked={expiringSoon} onChange={setExpiringSoon} />
            </span>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Compliance watchlist"
        description="Turn the filter off to see all tracked documents. Expiry items are sourced from employee profiles and HR document registers — renew before status turns Expired. Daily expiry alert emails are sent by the backend scheduler; no manual job is required from this page."
      />
      <Card size="small">
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  expiringSoon
                    ? 'Nothing expiring in the next 30 days'
                    : 'No document expiry records yet'
                }
              />
            ),
          }}
        />
      </Card>
    </WorkspaceLayout>
  );
};

export default DocumentExpiryPage;
